import React, { useEffect, useState } from 'react';
import { Avatar, Box, HStack, Td, Text, Tr, useToast } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';

import { useStore } from '../../../hooks/useStore';
import useDebounce from '../../../hooks/useDebounce';
import SearchBar from '../../../components/common/SearchBar';
import CustomTable from '../../../components/table/CustomTable';
import { chatHeaders } from '../../../constants/tableHeaders';
import { parseDate } from '../../../utils/date';
import DeleteMenu from '../../../components/common/DeleteMenu';

const ChatScreen = () => {
  const [currentPage, setCurrentPage] = useState(1);

  const {
    chatStore: {
      chats,
      fetchChats,
      totalChats,
      chatSearch,
      setChatSearch,
      deleteChat,
      loading,
    },
  } = useStore();

  const toast = useToast();

  const debouncedSearch = useDebounce(chatSearch, 500);

  useEffect(() => {
    setCurrentPage(1);
    (async () => await fetchChats({ page: 1, limit: 10 }))();
  }, [debouncedSearch]);

  const handleDelete = async (id: string) => {
    try {
      await deleteChat(id);
      toast({
        title: 'Chat deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: 'Failed to delete chat',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <SearchBar
        search={chatSearch}
        setSearch={setChatSearch}
        placeholder={'Search for chat users'}
      />
      <CustomTable
        headers={chatHeaders}
        rowsPerPage={10}
        totalRows={totalChats}
        fetchDataTable={fetchChats}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
      >
        {(chats || []).map((chat) => {
          const parsedDate = parseDate(chat.updatedAt.toString());

          return (
            <Tr key={chat.id}>
              <Td>
                <HStack>
                  <Avatar
                    size="sm"
                    src={chat?.user?.avatar || ''}
                    name={chat.user.userName || ''}
                  />
                  <Text>{chat.user.userName || '-'}</Text>
                </HStack>
              </Td>
              <Td>
                <HStack>
                  <Avatar
                    size="sm"
                    src={chat?.otherUser?.avatar || ''}
                    name={chat.otherUser.userName || ''}
                  />
                  <Text>{chat.otherUser.userName || '-'}</Text>
                </HStack>
              </Td>
              <Td>
                {parsedDate.date}
                <br />
                {parsedDate.time}
              </Td>
              <Td>{chat.lastMessage ? chat.lastMessage : 'No messages'}</Td>
              <Td>
                {chat.blockedUser ? (
                  <HStack>
                    <Avatar
                      size="sm"
                      src={chat?.blockedUser?.avatar || ''}
                      name={chat.blockedUser.userName}
                    />
                    <Text>{chat.blockedUser.userName}</Text>
                  </HStack>
                ) : (
                  <Text>No blocked user</Text>
                )}
              </Td>
              <Td>
                <DeleteMenu
                  title={'Delete chat?'}
                  subtitle={'Are you sure you want to delete this chat?'}
                  cancelActionLabel={'No, keep it'}
                  confirmActionLabel={'Delete chat'}
                  onDelete={() => handleDelete(chat.id)}
                  deleteActionLabel={'Delete chat'}
                />
              </Td>
            </Tr>
          );
        })}
      </CustomTable>
    </Box>
  );
};

export default observer(ChatScreen);
